import { Link, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { ZONE2_NAV_ITEMS, isNavActive } from '@/features/layout/logic';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function LogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

// Simplified icon map for mobile (reuse icon names)
const ICON_LABELS: Record<string, string> = {
  inbox: 'IN', chat: 'CH', task: 'TA', document: 'DO',
  calendar: 'CA', team: 'TE', project: 'PR', workflow: 'WF',
};

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchOpen: () => void;
  onQuickCreate: () => void;
  onAppLauncher: () => void;
  onTenantSwitcher: () => void;
  notificationCount: number;
  userInitial: string;
}

export const MobileSidebar = observer(({
  isOpen,
  onClose,
  onSearchOpen,
  onQuickCreate,
  onAppLauncher,
  onTenantSwitcher,
  notificationCount,
  userInitial,
}: MobileSidebarProps) => {
  const { t } = useI18n();
  const location = useLocation();

  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-40">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <nav
        aria-label={t('layout.sidebar.label')}
        className="absolute inset-y-0 left-0 w-72 bg-sidebar border-r border-sidebar-border shadow-lg flex flex-col animate-in slide-in-from-left duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
          <Link to="/inbox" onClick={onClose} className="flex items-center gap-2">
            <LogoIcon className="h-6 w-6 text-primary" />
            <span className="text-sm font-semibold text-sidebar-foreground">Corde</span>
          </Link>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground"
            aria-label={t('common.close')}
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Search + Quick Create */}
        <div className="px-3 py-2 space-y-1 border-b border-sidebar-border">
          <button onClick={onSearchOpen} className="flex items-center gap-3 w-full h-11 px-3 rounded-md text-sm text-muted-foreground hover:bg-sidebar-accent transition-colors">
            <SearchIcon className="h-4 w-4" />
            {t('layout.sidebar.search')}
          </button>
          <button onClick={onQuickCreate} className="flex items-center gap-3 w-full h-11 px-3 rounded-md text-sm text-muted-foreground hover:bg-sidebar-accent transition-colors">
            <PlusIcon className="h-4 w-4" />
            {t('layout.sidebar.quickCreate')}
          </button>
        </div>

        {/* Navigation items */}
        <div className="flex-1 overflow-y-auto scrollbar-slim px-3 py-2 space-y-0.5">
          {ZONE2_NAV_ITEMS.map((item) => {
            const active = isNavActive(location.pathname, item.href);
            return (
              <Link
                key={item.id}
                to={item.href}
                onClick={onClose}
                className={`flex items-center gap-3 h-11 px-3 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <span className="h-5 w-5 flex items-center justify-center text-[10px] font-bold">
                  {ICON_LABELS[item.icon] || '??'}
                </span>
                {t(item.labelKey)}
              </Link>
            );
          })}
        </div>

        {/* Bottom */}
        <div className="px-3 py-3 border-t border-sidebar-border space-y-1">
          {/* App Launcher */}
          <button onClick={onAppLauncher} className="flex items-center gap-3 w-full h-11 px-3 rounded-md text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors">
            <span className="h-5 w-5 flex items-center justify-center text-[10px] font-bold">AP</span>
            {t('layout.sidebar.appLauncher')}
          </button>

          {/* Notifications */}
          <Link to="/notifications" onClick={onClose} className="flex items-center gap-3 w-full h-11 px-3 rounded-md text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors">
            <span className="h-5 w-5 flex items-center justify-center text-[10px] font-bold">NO</span>
            {t('layout.sidebar.notifications')}
            {notificationCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium px-1">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </Link>

          {/* Profile */}
          <button onClick={onTenantSwitcher} className="flex items-center gap-3 w-full h-11 px-3 rounded-md text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors">
            <span className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">
              {userInitial}
            </span>
            {t('layout.sidebar.profile')}
          </button>
        </div>
      </nav>
    </div>
  );
});
